import React, { useEffect, useState } from 'react'
import Typography from '@material-ui/core/Typography'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import TimerIcon from '@material-ui/icons/Timer'
import makeStyles from '@material-ui/core/styles/makeStyles'
import MerchantCard from '../../Cards/MerchantCard'
import { pushToDataLayer } from '../../../utils/dataLayer'
import {
    capitalizeFirstLetter,
    formatAmount,
    getCartItemProductGiftRecipientName,
    getCartItemProductGiftAddress,
    getCartItemProductImage,
} from '../../../utility'
import PromoCodeInput from '../../Base/CartDrawer/PromoCodeInput'
import Box from '@material-ui/core/Box'
import Fade from '@material-ui/core/Fade'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import useTheme from '@material-ui/core/styles/useTheme'
import isEmpty from 'lodash/isEmpty'
import ConsumerCheckoutPurchaseReminder from './ConsumerCheckoutPurchaseReminder'
import { OPEN_LOOP_BRAND_SLUGS } from '../../../constants'
import get from 'lodash/get'
import ShippingCopy from './ShippingCopy'
import MailOutlineIcon from '@material-ui/icons/MailOutline'

const useStyles = makeStyles((theme) => ({
    orderSummaryContainer: {
        width: '100%',
        color: '#505E71',
    },
    orderSummaryHeading: {
        fontSize: '1.25rem',
        fontWeight: 'bold',
        marginTop: 0,
        marginBottom: '1.35rem',
    },
    headerColor: {
        color: '#3B1A6E',
    },
    cartItemContainer: {
        marginBottom: '1.25rem',
        borderBottom: `2px solid #CDCDCD`,
    },
    cartItem: {
        alignItems: 'flex-start',
        paddingTop: 0,
        paddingBottom: 10,
        [theme.breakpoints.up('sm')]: {
            columnGap: '.8rem',
        },
    },
    cartItemImage: {
        width: '27%',
        height: 'auto',
        flexGrow: 0,
        borderRadius: 8,
    },
    cartItemDetailsContainer: {
        paddingLeft: 5,
        flexGrow: 1,
        [theme.breakpoints.up('sm')]: {
            paddingLeft: 0,
        },
    },
    cartItemTotalContainer: {
        flexGrow: 0,
    },
    cartItemEditLink: {
        padding: '0.5em 0 0.25em',
        '& a': {
            color: '#505E71',
            fontWeight: 'bold',
            fontSize: '14px',
            textDecoration: 'underline',
        },
    },
    trashButton: {
        zIndex: 1,
        alignSelf: 'flex-start',
        padding: 0,
        paddingLeft: 3,
        color: theme.palette.black.main,
        '&:hover': {
            backgroundColor: 'transparent',
        },
    },
    cartItemProductName: {
        fontSize: '1rem',
        fontWeight: 'bold',
    },
    cartItemType: {
        fontSize: '0.75rem',
    },
    cartItemPrice: {
        fontSize: '0.875rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        paddingLeft: 4,
        [theme.breakpoints.up('sm')]: {
            paddingLeft: 0,
        },
    },
    cartItemCashback: {
        whiteSpace: 'nowrap',
        fontSize: '0.75rem',
    },
    giftDetailsContainer: {
        paddingBottom: '1rem',
    },
    giftDetails: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.875rem',
    },
    giftDetailsIcon: {
        fontSize: '1rem',
        marginRight: 3,
        stroke: '#333',
        strokeWidth: '.75',
    },
    cartItemPromotionLabel: {
        fontSize: '0.875rem',
        fontWeight: 'bold',
        backgroundColor: '#01AEA4',
        color: '#FFFFFF',
        borderRadius: 5,
        padding: 10,
        marginTop: 10,
    },
    orderSummaryTotalContainer: {
        marginTop: '-0.4rem',
    },
    orderSummaryTotalSection: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0 0 0.75rem 0',
        marginBottom: '0.75rem',
        borderBottom: `2px solid #CDCDCD`,
    },
    orderSummaryText: {
        fontSize: '1rem',
    },
    orderTotalText: {
        fontSize: '1.25rem',
        fontWeight: 'bold',
        '&:last-child': {
            color: theme.palette.primary.main,
        },
    },
    deliveryNote: {
        fontSize: '0.875rem',
        textWrap: 'pretty',
    },
}))

const OrderSummary = ({
    user,
    cart,
    applyPromoIsLoading,
    removePromoIsLoading,
    errors,
    removeCard,
    handleApplyPromoCode,
    handleRemovePromoCode,
}) => {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
        defaultMatches: true,
    })
    const promoIsLoading = applyPromoIsLoading

    useEffect(() => {
        pushToDataLayer(['loadedCart']) // Needed by GTM to fire off 'HTML - EEC - checkoutStep1' tag
    }, [])

    useEffect(() => {
        if (user?.is_logged_in) {
            window.dataLayer.push({
                GCG_USER_ID: user.user_id, // Some GTM cart-related tags have event trigger conditions that depend on the data layer GCG_USER_ID variable being defined
            })
            pushToDataLayer(['fireCheckoutStep2']) // Needed by GTM to fire off 'HTML - EEC - checkoutStep2' tag
        }
    }, [user])


    const {
        cart_items: cartItems,
        cart_id: cartId,
        delivery_groups: deliveryGroups,
        promo_code_redemption: promoCodeRedemption,
        total_price: totalPrice,
        total_customization_fees: totalCustomizationFees,
        total_handwrytten_fees: totalHandwryttenFees,
        total_greeting_card_fees: totalGreetingCardFees,
        total_processing_fees: totalProcessingFees,
        total_shipping_fees: totalShippingFees,
        total_cash_back: totalCashback,
        order_total: orderTotal,
        total_quantity: totalQuantity,
        total_discount: totalDiscount,
    } = cart

    const shippingFeeWithDiscount =
        totalShippingFees - (promoCodeRedemption?.shipping_fee_discount ?? 0)
    const processingFeeWithDiscount =
        totalProcessingFees -
        (promoCodeRedemption?.processing_fee_discount ?? 0)
    const greetingCardFeeWithDiscount =
        totalGreetingCardFees -
        (promoCodeRedemption?.greeting_card_fee_discount ?? 0)
    const handwryttenFeeWithDiscount = totalHandwryttenFees //No discount functionality for Handwrytten yet
    const customizationFeeWithDiscount =
        totalCustomizationFees -
        (promoCodeRedemption?.customization_fee_discount ?? 0)
    const loadValueWithDiscount =
        totalPrice - (promoCodeRedemption?.load_value_discount ?? 0)

    const parseGtmMerchantName = (cartItem) => {
        const merchantSlug = get(cartItem, 'merchant.slug')
        if (OPEN_LOOP_BRAND_SLUGS.includes(merchantSlug)) {
            return capitalizeFirstLetter(merchantSlug)
        }
        return get(cartItem, 'merchant.name')
    }

    const parseGtmProductId = (cartItem) => {
        const merchantSlug = get(cartItem, 'merchant.slug')
        if (OPEN_LOOP_BRAND_SLUGS.includes(merchantSlug)) {
            let productId = capitalizeFirstLetter(merchantSlug)
            if (!get(cartItem, 'plastic')) {
                productId += ' Virtual'
            }
            return productId
        }
        return get(cartItem, 'merchant.name')
    }

    return (
        <div className={classes.orderSummaryContainer}>
            <Typography
                variant="h1"
                className={`${classes.orderSummaryHeading} ${classes.headerColor}`}
            >
                Order Summary ({totalQuantity} Item
                {totalQuantity > 1 && <>s</>})
            </Typography>
            <List id="order-summary" disablePadding>
                {cartItems.map((cartItem) => {
                    const {
                        delivery_group_uuid: deliveryGroupUuid,
                        item_id: cartItemId,
                        plastic,
                        product_name: productName,
                        price: cardPrice,
                        cash_back: cardCashBack,
                        delivery_estimate: deliveryEstimate,
                        open_loop: isOpenLoop,
                        gift: isGift,
                        build_a_card: isBuildACard,
                        custom_card_details: customCardDetails,
                        promotional_offer: promotionalOffer,
                        greeting_card: greetingCard,
                        sku,
                        quantity,
                        merchant,
                        balance,
                        shipping,
                        item_edit_url: itemEditUrl,
                    } = cartItem
                    const cartItemDeliveryGroup = deliveryGroups.find(
                        (d) => d.uuid === deliveryGroupUuid
                    )
                    const productImageUrl = getCartItemProductImage(cartItem)
                    
                    const formattedProductName = productName
                        .replace(/virtual\s/i, '')
                        .replace( /eGift/gi, isOpenLoop ? 'Gift' : 'Digital Gift')

                    return (
                        <div
                            key={cartItemId}
                            className={`${classes.cartItemContainer} cart-item`} // Need cart-item class for GTM checkout step 1 tag
                            data-id={cartItemId}
                            data-sku={sku || ''}
                            data-quantity={quantity}
                            data-merchant={parseGtmMerchantName(cartItem)}
                            data-product-id={parseGtmProductId(cartItem)}
                            data-merchant-id={merchant.id}
                            data-balance={balance}
                            data-price={cardPrice}
                            data-cash-back={cardCashBack}
                            data-discount-percentage={
                                ((balance - cardPrice) / balance) * 100
                            }
                            data-is-gift={isGift ? 1 : 0}
                            data-is-open-loop={isOpenLoop ? 1 : 0}
                            data-is-build-a-card={isBuildACard ? 1 : 0}
                            data-shipping-method-id={
                                shipping && shipping.shipping_method
                                    ? shipping.shipping_method.id
                                    : null
                            }
                        >
                            <ListItem
                                className={classes.cartItem}
                                disableGutters
                            >
                                <Fade in timeout={300}>
                                    <MerchantCard
                                        imageSrc={productImageUrl}
                                        title={
                                            isOpenLoop
                                                ? `${customCardDetails?.wcp_product?.name} Gift Card`
                                                : formattedProductName
                                        }
                                        bordered
                                        additionalStyles={classes.cartItemImage}
                                        lazyLoad={true}
                                    />
                                </Fade>
                                <Box
                                    className={classes.cartItemDetailsContainer}
                                >
                                    <Typography
                                        className={classes.cartItemProductName}
                                        data-testid={`cart-item-product-name-${cartItemId}`}
                                    >
                                        {formattedProductName}
                                    </Typography>
                                    <Typography
                                        className={classes.cartItemType}
                                    >
                                        Type: {plastic ? 'Plastic' : 'Digital'}
                                    </Typography>
                                    {!isEmpty(greetingCard) && (
                                        <Typography
                                            className={classes.cartItemType}
                                        >
                                            With a greeting card
                                        </Typography>
                                    )}
                                    {quantity > 1 && (
                                        <Typography
                                            className={classes.cartItemType}
                                        >
                                            Quantity: {quantity}
                                        </Typography>
                                    )}
                                    {itemEditUrl && (
                                        <Typography
                                            className={classes.cartItemEditLink}
                                        >
                                            <a href={itemEditUrl}>Edit</a>
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    display="flex"
                                    className={classes.cartItemTotalContainer}
                                >
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="flex-end"
                                    >
                                        <Typography
                                            className={classes.cartItemPrice}
                                        >
                                            ${formatAmount(cardPrice)}
                                        </Typography>
                                        {cardCashBack > 0 && (
                                            <Typography
                                                className={
                                                    classes.cartItemCashback
                                                }
                                            >
                                                ${formatAmount(cardCashBack)}{' '}
                                                cash back
                                            </Typography>
                                        )}
                                    </Box>

                                    <IconButton
                                        className={`${classes.trashButton}`}
                                        aria-label="Delete cart item"
                                        onClick={() =>
                                            removeCard(cartId, cartItemId)
                                        }
                                        classes={{
                                            label: 'trash', // for GTM 'Click Classes' trigger condition
                                        }}
                                        disableRipple
                                    >
                                        <DeleteForeverIcon
                                            style={{ zIndex: -1 }}
                                        />
                                    </IconButton>
                                </Box>
                            </ListItem>
                            <div className={classes.giftDetailsContainer}>
                                <Typography className={classes.giftDetails}>
                                    <MailOutlineIcon
                                        className={classes.giftDetailsIcon}
                                    />
                                    To:{' '}
                                    {getCartItemProductGiftRecipientName(
                                        cartItem,
                                        cartItemDeliveryGroup
                                    )}
                                    ,
                                    {getCartItemProductGiftAddress(
                                        cartItem,
                                        cartItemDeliveryGroup
                                    )}
                                </Typography>
                                <Typography
                                    className={classes.giftDetails}
                                    style={{ margin: '0.25rem 0' }}
                                >
                                    <TimerIcon
                                        className={classes.giftDetailsIcon}
                                    />
                                    {cartItemDeliveryGroup && plastic
                                        ? cartItemDeliveryGroup.shipping_method
                                              .delivery_estimate
                                        : deliveryEstimate}
                                </Typography>
                                {isOpenLoop && plastic && (
                                    <Typography
                                        className={classes.deliveryNote}
                                    >
                                        For same-day shipping order before 4 PM
                                        ET Mon-Fri. Orders after 4 PM ET will
                                        generally ship the next business day.{' '}
                                        <ShippingCopy />
                                    </Typography>
                                )}
                                {promotionalOffer && (
                                    <Box
                                        className={
                                            classes.cartItemPromotionLabel
                                        }
                                    >
                                        <span style={{ display: 'inline' }}>
                                            {promotionalOffer.label}
                                        </span>
                                    </Box>
                                )}
                            </div>
                        </div>
                    )
                })}
            </List>
            <Box className={classes.orderSummaryTotalContainer}>
                <Box className={classes.orderSummaryTotalSection}>
                    <Box>
                        {totalDiscount > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                Promo Discount
                            </Typography>
                        )}
                        <Typography className={classes.orderSummaryText}>
                            {promoCodeRedemption &&
                            promoCodeRedemption.load_value_discount > 0
                                ? 'Card Value Paid'
                                : 'Gift Card Value'}
                        </Typography>
                        <Typography className={classes.orderSummaryText}>
                            Shipping
                        </Typography>
                        <Typography className={classes.orderSummaryText}>
                            Processing Fees
                        </Typography>
                        {totalCustomizationFees > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                Personalization Fees
                            </Typography>
                        )}
                        {totalGreetingCardFees > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                Greeting Card Fees
                            </Typography>
                        )}
                        {totalHandwryttenFees > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                Handwritten Fees
                            </Typography>
                        )}
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-end"
                    >
                        {totalDiscount > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                -${formatAmount(totalDiscount)}
                            </Typography>
                        )}
                        <Typography className={classes.orderSummaryText}>
                            ${formatAmount(loadValueWithDiscount)}
                        </Typography>
                        <Typography className={classes.orderSummaryText}>
                            ${formatAmount(shippingFeeWithDiscount)}
                        </Typography>
                        <Typography className={classes.orderSummaryText}>
                            ${formatAmount(processingFeeWithDiscount)}
                        </Typography>
                        {totalCustomizationFees > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                ${formatAmount(customizationFeeWithDiscount)}
                            </Typography>
                        )}
                        {totalGreetingCardFees > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                ${formatAmount(greetingCardFeeWithDiscount)}
                            </Typography>
                        )}
                        {totalHandwryttenFees > 0 && (
                            <Typography className={classes.orderSummaryText}>
                                ${formatAmount(handwryttenFeeWithDiscount)}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box className={classes.orderSummaryTotalSection}>
                    <Typography
                        className={`${classes.orderTotalText} ${classes.headerColor}`}
                    >
                        Order Total
                    </Typography>
                    <Typography className={classes.orderTotalText}>
                        ${formatAmount(orderTotal)}
                    </Typography>
                </Box>

                {totalCashback > 0 && (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        style={{ marginBottom: '0.75rem' }}
                    >
                        <Typography className={classes.orderSummaryText}>
                            You Earn
                        </Typography>
                        <Typography className={classes.orderSummaryText}>
                            ${formatAmount(totalCashback)} cash back
                        </Typography>
                    </Box>
                )}
                <Box>
                    <PromoCodeInput
                        appliedPromoCode={promoCodeRedemption?.redemption_code}
                        appliedPromoCodeTerms={promoCodeRedemption?.terms}
                        hasQualifiedItems={
                            promoCodeRedemption?.has_qualified_items
                        }
                        applyPromoIsLoading={promoIsLoading}
                        removePromoIsLoading={removePromoIsLoading}
                        errors={errors}
                        handleApplyPromoCode={handleApplyPromoCode}
                        handleRemovePromoCode={handleRemovePromoCode}
                    />
                </Box>
                <Box>
                    <ConsumerCheckoutPurchaseReminder />
                </Box>
            </Box>
        </div>
    )
}

export default OrderSummary
